/**
 * EasyApplyPage — one-page Easy Apply form (/easyApplyApplication/:id).
 * Mirrors Flutter WebCandidateEasyApplyApplicationScreen + WebCandidateEasyApplyApplicationForm:
 * job information card → WebEasyApplyResume → WebEasyApplyBasicInformation → Apply.
 * Logic lives in useEasyApply; the card, fields and footer share the application form's styles.
 */
import type { ReactNode } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { useLocalization } from '@/hooks/useLocalization';
import { Toast } from '@/features/home/components/Toast';
import { JobSummary } from './ApplicationFormPage';
import { useEasyApply } from './hooks/useEasyApply';
import type { EasyApplyState } from './hooks/useEasyApply';
import { ResumeUpload } from './components/ResumeUpload';
import { DynamicFormField } from './components/DynamicFormField';
import formStyles from './ApplicationFormPage.module.css';
import styles from './EasyApplyPage.module.css';

/** Flutter WebCandidateApplicationFormConstants.confirmEditAppliedJob */
const CONFIRM_EDIT_APPLIED =
  'You have already applied to this job. Do you want to edit the application submitted?';

type Actions = ReturnType<typeof useEasyApply>['actions'];
type T = (key: string) => string;

export function EasyApplyPage() {
  const { t } = useLocalization();
  const { state, actions } = useEasyApply();
  const app = state.application;
  const isBusy = state.isSubmitting || state.isUploading;

  // Without a form (e.g. /easyApplyApplication with no job id) Flutter shows the job card and the
  // resume section only; Basic Information and its Apply button need the form
  return (
    <PageLayout>
      <div className={formStyles.container}>
        {state.isInitializing ? (
          <EasyApplyShimmer />
        ) : (
          <>
            {/* Flutter's Easy Apply job card has no title line (web_candidate_easy_apply_application_form.dart) */}
            <JobSummary profileInput={state.profileInput} />

            <div className={formStyles.formCard}>
              <div className={formStyles.stepBody} aria-busy={isBusy}>
                <Block number={1} title={t('Upload Resume')} done={state.resumeUploaded}>
                  <ResumeUpload
                    resumeUrl={state.resumeUrl}
                    resumeUploaded={state.resumeUploaded}
                    isLoading={state.isUploading}
                    isLoadingResumeData={state.isLoadingResumeData}
                    uploadProgress={state.uploadProgress}
                    onFileSelect={actions.selectResume}
                    onDownload={actions.downloadResume}
                    onDelete={actions.deleteResume}
                    showTitle={false}
                  />
                </Block>

                {app && (
                  <Block number={2} title={app.personalInformation.headerTitle}>
                    <BasicInformation state={state} actions={actions} />
                  </Block>
                )}

                {isBusy && <div className={formStyles.busyVeil} aria-hidden="true" />}
              </div>

              {app && (
                <div className={formStyles.footer}>
                  <span />
                  <button
                    type="button"
                    className={formStyles.primaryBtn}
                    disabled={isBusy}
                    // Keep focus in the field: its blur error would shift the button before the click lands
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={actions.apply}
                  >
                    {state.isSubmitting && <span className={formStyles.btnSpinner} aria-hidden="true" />}
                    {t('Apply')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {state.showContinuePopup && <ContinuePopup actions={actions} t={t} />}

        {state.toast && <Toast message={state.toast.message} type={state.toast.type} onClose={actions.clearToast} />}
      </div>
    </PageLayout>
  );
}

/* ────────────────────────────── Sections ────────────────────────────── */

/** A numbered section; the title is left out when empty (Flutter hides an empty headerTitle). */
function Block({
  number,
  title,
  done,
  children,
}: {
  number: number;
  title?: string;
  done?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={`${styles.block} ${done ? styles.blockDone : ''}`}>
      <div className={styles.blockHead}>
        <span className={styles.blockNumber} aria-hidden="true">
          {done ? (
            <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="m4.5 10.5 3.5 3.5 7.5-8" />
            </svg>
          ) : (
            number
          )}
        </span>
        {title && <h2 className={styles.blockTitle}>{title}</h2>}
      </div>
      <div className={styles.blockContent}>{children}</div>
    </section>
  );
}

/**
 * Flutter WebEasyApplyBasicInformation._buildPersonalInformationFields — one field per cell;
 * the experience years field carries the months field that follows it.
 */
function BasicInformation({ state, actions }: { state: EasyApplyState; actions: Actions }) {
  const fields = state.application?.personalInformation.fields ?? [];
  const cells: ReactNode[] = [];

  for (let index = 0; index < fields.length; index++) {
    const field = fields[index];
    const monthsIndex =
      field.key === 'experienceInYears' && fields[index + 1]?.key === 'experienceInMonths' ? index + 1 : -1;
    const months = monthsIndex >= 0 ? fields[monthsIndex] : undefined;
    cells.push(
      <div key={`${field.key || 'field'}-${index}`} className={formStyles.fieldCell}>
        <DynamicFormField
          field={field}
          onChange={(value, display) => actions.updateField(index, value, display)}
          error={state.errors[index] ?? (months ? state.errors[monthsIndex] : undefined)}
          monthsField={months}
          onMonthsChange={months ? (value) => actions.updateField(monthsIndex, value) : undefined}
        />
      </div>,
    );
    if (months) index++;
  }

  return <div className={formStyles.fieldsGrid}>{cells}</div>;
}

/** Flutter CustomDialog.confirmDialog with no header: the question, Cancel and Proceed. */
function ContinuePopup({ actions, t }: { actions: Actions; t: T }) {
  return (
    <div className={formStyles.modalBackdrop} role="presentation">
      <div className={formStyles.modal} role="dialog" aria-modal="true" aria-labelledby="easy-apply-edit-text">
        <p id="easy-apply-edit-text" className={formStyles.modalText}>{t(CONFIRM_EDIT_APPLIED)}</p>
        <div className={formStyles.modalActions}>
          <button type="button" className={formStyles.secondaryBtn} onClick={() => actions.dismissContinuePopup(false)}>
            {t('Cancel')}
          </button>
          <button type="button" className={formStyles.primaryBtn} onClick={() => actions.dismissContinuePopup(true)} autoFocus>
            {t('Proceed')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── Loading ────────────────────────────── */

function EasyApplyShimmer() {
  return (
    <div aria-hidden="true">
      <div className={`${formStyles.jobCard} ${formStyles.shimmerCard}`}>
        <span className={`${formStyles.shimmerLine} ${formStyles.shimmerEyebrow}`} />
        <span className={`${formStyles.shimmerLine} ${formStyles.shimmerTitle}`} />
        <span className={`${formStyles.shimmerLine} ${formStyles.shimmerMeta}`} />
      </div>
      <div className={formStyles.formCard}>
        <div className={formStyles.stepBody}>
          <span className={`${formStyles.shimmerLine} ${formStyles.shimmerHeading}`} />
          <span className={`${formStyles.shimmerLine} ${styles.shimmerDropzone}`} />
          <span className={`${formStyles.shimmerLine} ${formStyles.shimmerHeading}`} />
          <div className={formStyles.fieldsGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={formStyles.shimmerField}>
                <span className={`${formStyles.shimmerLine} ${formStyles.shimmerLabel}`} />
                <span className={`${formStyles.shimmerLine} ${formStyles.shimmerInput}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
